import {
  type ClippingFetchResponse,
  ClippingFetchResponseSchema,
  type ExtractionItem,
  ExtractionItemSchema,
  type ExtractionSourceKind,
  type ExtractionView,
  ExtractionViewSchema,
  ExtractionListResponseSchema,
  type OpenExtractionsSummary,
  OpenExtractionsSummarySchema,
  type HubDecomposeResult,
  HubDecomposeResultSchema,
  type IdeationAssembledContext,
  IdeationAssembledContextSchema,
  type IdeationChatAnswer,
  IdeationChatAnswerSchema,
  type IdeationChatMessageListResponse,
  IdeationChatMessageListResponseSchema,
  type IdeationChatScope,
  type IdeationChatSessionListResponse,
  IdeationChatSessionListResponseSchema,
  type IdeationMap,
  type IdeationMapCreateInput,
  type IdeationMapEdge,
  IdeationMapEdgeSchema,
  type IdeationMapEdgeCreate,
  type IdeationMapListResponse,
  IdeationMapListResponseSchema,
  type IdeationMapNode,
  type IdeationMapNodeCreate,
  type IdeationMapNodeListResponse,
  IdeationMapNodeListResponseSchema,
  IdeationMapNodeSchema,
  type IdeationMapNodeUpdate,
  IdeationMapSchema,
  type IdeationMapUpdateInput,
  type IdeationMapView,
  IdeationMapViewSchema,
  type IdeationMapLink,
  IdeationMapLinkSchema,
  IdeationMapLinkListResponseSchema,
  type IdeationMapLinkCreateInput,
  type IdeationMapLinkListResponse,
  type MemberDisplay,
  type MapTaxonomy,
  type MapTaxonomyResponse,
  MapTaxonomyResponseSchema,
  type NodeDocumentView,
  NodeDocumentViewSchema,
  type NodeTaxonomy,
  type NodeTaxonomyResponse,
  NodeTaxonomyResponseSchema,
  type ThemeProposal,
  ThemeProposalSchema,
  type VaultDocument,
  VaultDocumentSchema,
  type IdeationNodeSupersedeBody,
  SupersedeNodeResultSchema,
  type SupersedeNodeResult,
  readStaffTenantSlug,
  subscribeStaffTenantChange,
} from "@l30/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { apiFetch, apiFetchUnchecked } from "./client.js";

export const IDEATION_MAPS_KEY = ["ideation-maps"] as const;
const MAPS_KEY = [...IDEATION_MAPS_KEY, "maps"] as const;
const NODES_KEY = [...IDEATION_MAPS_KEY, "nodes"] as const;
const CHAT_KEY = [...IDEATION_MAPS_KEY, "chat"] as const;
const EXTRACTIONS_KEY = [...IDEATION_MAPS_KEY, "extractions"] as const;
const MAP_TAXONOMY_KEY = [...IDEATION_MAPS_KEY, "taxonomy", "map-kinds"] as const;
const NODE_TAXONOMY_KEY = [...IDEATION_MAPS_KEY, "taxonomy", "node-kinds"] as const;
const ATTACHMENTS_FOLDER_KEY = [...IDEATION_MAPS_KEY, "attachments-folder"] as const;

function useStaffTenantCacheScope(): string {
  const [scope, setScope] = useState(() => readStaffTenantSlug() ?? "host-default");
  useEffect(() => {
    return subscribeStaffTenantChange(() => setScope(readStaffTenantSlug() ?? "host-default"));
  }, []);
  return scope;
}

// ─── Kind taxonomies (per-tenant map/node kind vocabularies) ─────────────────
export function useMapTaxonomy() {
  return useQuery({
    queryKey: MAP_TAXONOMY_KEY,
    queryFn: () =>
      apiFetch<MapTaxonomyResponse>(
        "/ideation-maps/taxonomy/map-kinds",
        {},
        MapTaxonomyResponseSchema,
      ),
  });
}

export function useNodeTaxonomy() {
  return useQuery({
    queryKey: NODE_TAXONOMY_KEY,
    queryFn: () =>
      apiFetch<NodeTaxonomyResponse>(
        "/ideation-maps/taxonomy/node-kinds",
        {},
        NodeTaxonomyResponseSchema,
      ),
  });
}

export function useSaveMapTaxonomy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taxonomy: MapTaxonomy) =>
      apiFetch<MapTaxonomyResponse>(
        "/ideation-maps/taxonomy/map-kinds",
        { method: "PUT", body: JSON.stringify(taxonomy) },
        MapTaxonomyResponseSchema,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: MAP_TAXONOMY_KEY }),
  });
}

export function useSaveNodeTaxonomy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taxonomy: NodeTaxonomy) =>
      apiFetch<NodeTaxonomyResponse>(
        "/ideation-maps/taxonomy/node-kinds",
        { method: "PUT", body: JSON.stringify(taxonomy) },
        NodeTaxonomyResponseSchema,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: NODE_TAXONOMY_KEY }),
  });
}

// ─── Queries ────────────────────────────────────────────────────────────────
export function useIdeationMaps(options?: { enabled?: boolean }) {
  const tenantScope = useStaffTenantCacheScope();
  return useQuery<IdeationMapListResponse>({
    queryKey: [...MAPS_KEY, tenantScope],
    queryFn: () => apiFetch("/ideation-maps/maps", {}, IdeationMapListResponseSchema),
    enabled: options?.enabled ?? true,
  });
}

export function useMapLinks(mapId: string | null, opts?: { inbound?: boolean }) {
  const inbound = opts?.inbound ?? true;
  return useQuery<IdeationMapLinkListResponse>({
    queryKey: [...MAPS_KEY, mapId, "links", { inbound }],
    enabled: Boolean(mapId),
    queryFn: () =>
      apiFetch(
        `/ideation-maps/maps/${mapId}/links?inbound=${inbound ? "true" : "false"}`,
        {},
        IdeationMapLinkListResponseSchema,
      ),
  });
}

export function useCreateMapLink(mapId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: IdeationMapLinkCreateInput) =>
      apiFetch<IdeationMapLink>(
        `/ideation-maps/maps/${mapId}/links`,
        { method: "POST", body: JSON.stringify(input) },
        IdeationMapLinkSchema,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...MAPS_KEY, mapId, "links"] });
    },
  });
}

export function useDeleteMapLink(mapId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) =>
      apiFetchUnchecked<void>(`/ideation-maps/map-links/${linkId}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...MAPS_KEY, mapId, "links"] });
    },
  });
}

export function useIdeationMapView(mapId: string | null, opts?: { includeJourney?: boolean }) {
  const includeJourney = opts?.includeJourney ?? false;
  return useQuery<IdeationMapView>({
    queryKey: [...MAPS_KEY, mapId, { includeJourney }],
    enabled: Boolean(mapId),
    queryFn: () =>
      apiFetch(
        `/ideation-maps/maps/${mapId}${includeJourney ? "?includeJourney=true" : ""}`,
        {},
        IdeationMapViewSchema,
      ),
  });
}

export function useIdeationMapNodes(options?: { enabled?: boolean }) {
  const tenantScope = useStaffTenantCacheScope();
  return useQuery<IdeationMapNodeListResponse>({
    queryKey: [...NODES_KEY, tenantScope],
    queryFn: () => apiFetch("/ideation-maps/nodes", {}, IdeationMapNodeListResponseSchema),
    enabled: options?.enabled ?? true,
  });
}

// ─── Mutations ────────────────────────────────────────────────────────────────
function useInvalidateMaps() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: IDEATION_MAPS_KEY });
}

export function useCreateMapNode() {
  const invalidate = useInvalidateMaps();
  return useMutation({
    mutationFn: (input: IdeationMapNodeCreate) =>
      apiFetch<IdeationMapNode>(
        "/ideation-maps/nodes",
        { method: "POST", body: JSON.stringify(input) },
        IdeationMapNodeSchema,
      ),
    onSuccess: invalidate,
  });
}

export function useUpdateMapNode() {
  const invalidate = useInvalidateMaps();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: IdeationMapNodeUpdate }) =>
      apiFetch<IdeationMapNode>(
        `/ideation-maps/nodes/${id}`,
        { method: "PATCH", body: JSON.stringify(data) },
        IdeationMapNodeSchema,
      ),
    onSuccess: invalidate,
  });
}

export function useSupersedeMapNode() {
  const invalidate = useInvalidateMaps();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: IdeationNodeSupersedeBody }) =>
      apiFetch<SupersedeNodeResult>(
        `/ideation-maps/nodes/${id}/supersede`,
        { method: "POST", body: JSON.stringify(data) },
        SupersedeNodeResultSchema,
      ),
    onSuccess: invalidate,
  });
}

export function useDeleteMapNode() {
  const invalidate = useInvalidateMaps();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetchUnchecked(`/ideation-maps/nodes/${id}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });
}

export function useCreateMapEdge() {
  const invalidate = useInvalidateMaps();
  return useMutation({
    mutationFn: (input: IdeationMapEdgeCreate) =>
      apiFetch<IdeationMapEdge>(
        "/ideation-maps/edges",
        { method: "POST", body: JSON.stringify(input) },
        IdeationMapEdgeSchema,
      ),
    onSuccess: invalidate,
  });
}

export function useCreateMap() {
  const invalidate = useInvalidateMaps();
  return useMutation({
    mutationFn: (input: IdeationMapCreateInput) =>
      apiFetch<IdeationMap>(
        "/ideation-maps/maps",
        { method: "POST", body: JSON.stringify(input) },
        IdeationMapSchema,
      ),
    onSuccess: invalidate,
  });
}

export function useUpdateMap() {
  const invalidate = useInvalidateMaps();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: IdeationMapUpdateInput }) =>
      apiFetch<IdeationMap>(
        `/ideation-maps/maps/${id}`,
        { method: "PATCH", body: JSON.stringify(data) },
        IdeationMapSchema,
      ),
    onSuccess: invalidate,
  });
}

export function useArchiveMap() {
  const invalidate = useInvalidateMaps();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<IdeationMap>(
        `/ideation-maps/maps/${id}/archive`,
        { method: "POST" },
        IdeationMapSchema,
      ),
    onSuccess: invalidate,
  });
}

export function useDuplicateMap() {
  const invalidate = useInvalidateMaps();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<IdeationMap>(
        `/ideation-maps/maps/${id}/duplicate`,
        { method: "POST" },
        IdeationMapSchema,
      ),
    onSuccess: invalidate,
  });
}

export function useMergeMapNodes() {
  const invalidate = useInvalidateMaps();
  return useMutation({
    mutationFn: ({ keepId, dropId }: { keepId: string; dropId: string }) =>
      apiFetch<IdeationMapNode>(
        `/ideation-maps/nodes/${keepId}/merge`,
        { method: "POST", body: JSON.stringify({ dropId }) },
        IdeationMapNodeSchema,
      ),
    onSuccess: invalidate,
  });
}

export function useDuplicateMapNode() {
  const invalidate = useInvalidateMaps();
  return useMutation({
    mutationFn: ({ id, mapId }: { id: string; mapId?: string | null }) =>
      apiFetch<IdeationMapNode>(
        `/ideation-maps/nodes/${id}/duplicate${mapId ? `?mapId=${mapId}` : ""}`,
        { method: "POST" },
        IdeationMapNodeSchema,
      ),
    onSuccess: invalidate,
  });
}

export function useSetMapMember() {
  const invalidate = useInvalidateMaps();
  return useMutation({
    mutationFn: ({ mapId, nodeId, member }: { mapId: string; nodeId: string; member: boolean }) =>
      apiFetchUnchecked(`/ideation-maps/maps/${mapId}/members`, {
        method: "POST",
        body: JSON.stringify({ nodeId, member }),
      }),
    onSuccess: invalidate,
  });
}

export function useSetMemberDisplay() {
  const invalidate = useInvalidateMaps();
  return useMutation({
    mutationFn: ({
      mapId,
      nodeId,
      display,
    }: {
      mapId: string;
      nodeId: string;
      display: MemberDisplay;
    }) =>
      apiFetchUnchecked(`/ideation-maps/maps/${mapId}/members/display`, {
        method: "PATCH",
        body: JSON.stringify({ nodeId, display }),
      }),
    onSuccess: invalidate,
  });
}

// ─── Phase 2: context assembly + grounded chat (read loop) ──────────────────
/** Preview the token-budgeted, source-cited bundle Claude will be grounded on. */
export function useNodeContext(nodeId: string | null, depth = 1) {
  return useQuery<IdeationAssembledContext>({
    queryKey: [...CHAT_KEY, "context", "node", nodeId, depth],
    enabled: Boolean(nodeId),
    queryFn: () =>
      apiFetch(
        `/ideation-maps/nodes/${nodeId}/context?depth=${depth}`,
        {},
        IdeationAssembledContextSchema,
      ),
  });
}

/** Preview the token-budgeted, source-cited bundle for a whole map (no depth). */
export function useMapContext(mapId: string | null) {
  return useQuery<IdeationAssembledContext>({
    queryKey: [...CHAT_KEY, "context", "map", mapId],
    enabled: Boolean(mapId),
    queryFn: () =>
      apiFetch(`/ideation-maps/maps/${mapId}/context`, {}, IdeationAssembledContextSchema),
  });
}

/** Sessions for a node/map, most-recent first. */
export function useChatSessions(scope: IdeationChatScope, scopeId: string | null) {
  return useQuery<IdeationChatSessionListResponse>({
    queryKey: [...CHAT_KEY, "sessions", scope, scopeId],
    enabled: Boolean(scopeId),
    queryFn: () =>
      apiFetch(
        `/ideation-maps/chat/sessions?scope=${scope}&scopeId=${scopeId}`,
        {},
        IdeationChatSessionListResponseSchema,
      ),
  });
}

/** Full transcript for one session. */
export function useChatMessages(sessionId: string | null) {
  return useQuery<IdeationChatMessageListResponse>({
    queryKey: [...CHAT_KEY, "messages", sessionId],
    enabled: Boolean(sessionId),
    queryFn: () =>
      apiFetch(
        `/ideation-maps/chat/sessions/${sessionId}`,
        {},
        IdeationChatMessageListResponseSchema,
      ),
  });
}

export function useAskMapChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      scope: IdeationChatScope;
      scopeId: string;
      question: string;
      sessionId?: string | null;
      depth?: number;
    }) =>
      apiFetch<IdeationChatAnswer>(
        "/ideation-maps/chat",
        { method: "POST", body: JSON.stringify(input) },
        IdeationChatAnswerSchema,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: CHAT_KEY }),
  });
}

// ─── Phase 3: extraction → reviewed diff → merge (write loop) ───────────────
export function useExtraction(id: string | null) {
  return useQuery<ExtractionView>({
    queryKey: [...EXTRACTIONS_KEY, id],
    enabled: Boolean(id),
    queryFn: () => apiFetch(`/ideation-maps/extractions/${id}`, {}, ExtractionViewSchema),
    // The AI runs in the background (202 → "processing"); poll until it flips
    // to "pending" (items ready) or "failed". Stop polling once terminal.
    refetchInterval: (query) => (query.state.data?.status === "processing" ? 1500 : false),
  });
}

export function useRunExtraction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      sourceKind: ExtractionSourceKind;
      sessionId?: string | null;
      text?: string | null;
      mapId?: string | null;
      /** Raw upload content (transcript file, conversation export). */
      rawSource?: string | null;
      /** Ingestion format for `rawSource` (derived from the file extension). */
      format?: "markdown" | "claude-ai-json" | "claude-code-jsonl";
    }) =>
      apiFetch<ExtractionView>(
        "/ideation-maps/extractions",
        { method: "POST", body: JSON.stringify(input) },
        ExtractionViewSchema,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: EXTRACTIONS_KEY });
    },
  });
}

export function useReviewExtractionItem(extractionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      itemId,
      status,
    }: {
      itemId: string;
      status: "accepted" | "rejected" | "pending";
    }) =>
      apiFetch<ExtractionItem>(
        `/ideation-maps/extractions/${extractionId}/items/${itemId}`,
        { method: "PATCH", body: JSON.stringify({ status }) },
        ExtractionItemSchema,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...EXTRACTIONS_KEY, extractionId] });
      void qc.invalidateQueries({ queryKey: [...EXTRACTIONS_KEY, "summary"] });
    },
  });
}

export function useApplyExtraction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<ExtractionView>(
        `/ideation-maps/extractions/${id}/apply`,
        { method: "POST" },
        ExtractionViewSchema,
      ),
    // Apply mutates the graph and the extraction — refresh both.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: IDEATION_MAPS_KEY });
      void qc.invalidateQueries({ queryKey: EXTRACTIONS_KEY });
    },
  });
}

export function useDiscardExtraction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<ExtractionView>(
        `/ideation-maps/extractions/${id}/discard`,
        { method: "POST" },
        ExtractionViewSchema,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: EXTRACTIONS_KEY });
      void qc.invalidateQueries({ queryKey: IDEATION_MAPS_KEY });
    },
  });
}

/** Caller-scoped open extractions across maps (review drawer inbox). */
export function useOpenExtractionsList() {
  return useQuery({
    queryKey: [...EXTRACTIONS_KEY, "open", "all"],
    queryFn: () =>
      apiFetch("/ideation-maps/extractions?openOnly=true", {}, ExtractionListResponseSchema),
    refetchInterval: (query) =>
      query.state.data?.extractions.some((e) => e.status === "processing") ? 1500 : 30_000,
  });
}

/** Open (pending/processing) extractions for a map — MCP capture inbox. */
export function useMapExtractions(mapId: string | null, opts?: { openOnly?: boolean }) {
  const openOnly = opts?.openOnly ?? true;
  return useQuery({
    queryKey: [...EXTRACTIONS_KEY, "map", mapId, { openOnly }],
    enabled: Boolean(mapId),
    queryFn: () => {
      const params = new URLSearchParams();
      if (mapId) params.set("mapId", mapId);
      if (openOnly) params.set("openOnly", "true");
      return apiFetch(
        `/ideation-maps/extractions?${params.toString()}`,
        {},
        ExtractionListResponseSchema,
      );
    },
    // Poll while any extraction is still processing.
    refetchInterval: (query) =>
      query.state.data?.extractions.some((e) => e.status === "processing") ? 1500 : false,
  });
}

/** Caller-scoped open-extraction count for Atlas nav badge + maps home strip. */
export function useOpenExtractionsSummary() {
  return useQuery<OpenExtractionsSummary>({
    queryKey: [...EXTRACTIONS_KEY, "summary", "open"],
    queryFn: () => apiFetch("/ideation-maps/extractions/summary", {}, OpenExtractionsSummarySchema),
    refetchInterval: 30_000,
  });
}

/** Prefer a single-map deep link when the open queue is concentrated on one map. */
export function atlasHrefForOpenExtractions(summary: OpenExtractionsSummary | undefined): string {
  if (!summary || summary.count === 0) return "/ideation/maps";
  const mapped = summary.byMap.filter((b) => b.mapId);
  if (mapped.length === 1 && mapped[0]?.mapId) {
    return `/ideation/maps/${mapped[0].mapId}`;
  }
  return "/ideation/maps";
}

// ─── Ideation document upload ─────────────────────────────────────────────────

/**
 * Resolves (and auto-creates if absent) the well-known per-tenant
 * "Ideation attachments" folder. Backed by GET /ideation-maps/maps/attachments-folder.
 * Cached per session; invalidated only on mutation (rare).
 */
export function useIdeationAttachmentsFolder() {
  return useQuery<{ id: string }>({
    queryKey: ATTACHMENTS_FOLDER_KEY,
    queryFn: () => apiFetchUnchecked<{ id: string }>("/ideation-maps/maps/attachments-folder"),
  });
}

/**
 * Upload a File into the Vault under the well-known "Ideation attachments"
 * folder. Orchestrates the 3-step vault upload flow:
 *   1. GET /ideation-maps/maps/attachments-folder  → folderId
 *   2. POST /documents/upload-token                → { uploadUrl, blobKey }
 *   3. PUT uploadUrl (raw blob PUT to storage)
 *   4. POST /documents                             → VaultDocument
 *
 * Resolves to the created VaultDocument (id, filename, mimeType, sizeBytes, …).
 *
 * Raw `fetch` is used for the blob PUT (step 3) — it is a direct PUT to
 * the storage provider's signed URL, not an API call, so apiFetch must
 * not be used there.
 */
export function useUploadDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File): Promise<VaultDocument> => {
      // Step 1 — resolve the well-known ideation attachments folder.
      const { id: folderId } = await apiFetchUnchecked<{ id: string }>(
        "/ideation-maps/maps/attachments-folder",
      );

      // Step 2 — obtain a signed upload URL + a stable blobKey. Use the
      // ideation-scoped upload-token endpoint (gated on `ideation`, so
      // SALES/BOARD can use it) rather than /documents/upload-token (gated
      // on documents:write = ADMIN/EXEC). The server resolves the ideation
      // attachments folder itself; folderId here is advisory only.
      const { uploadUrl, blobKey } = await apiFetchUnchecked<{
        uploadUrl: string;
        blobKey: string;
        expiresAt: string;
      }>("/ideation-maps/maps/attachments/upload-token", {
        method: "POST",
        body: JSON.stringify({ filename: file.name, contentType: file.type, folderId }),
      });

      // Step 3 — PUT the raw file bytes to the signed URL.
      // This is a direct call to the storage provider (Azure Blob / R2),
      // NOT through the API. The x-ms-blob-type header is required by
      // Azure Blob Storage; R2 ignores it harmlessly.
      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "x-ms-blob-type": "BlockBlob",
          "Content-Type": file.type,
        },
        body: file,
      });
      if (!putRes.ok) {
        throw new Error(`Blob PUT failed: ${putRes.status} ${putRes.statusText}`);
      }

      // Step 4 — register the document row in the vault via the
      // ideation-scoped endpoint (same RBAC reasoning as Step 2). The API
      // forces the ideation attachments folder server-side and fires OCR
      // extraction in the background (fire-and-forget).
      const doc = await apiFetch<VaultDocument>(
        "/ideation-maps/maps/attachments/document",
        {
          method: "POST",
          body: JSON.stringify({
            blobKey,
            filename: file.name,
            title: file.name,
            mimeType: file.type,
            sizeBytes: file.size,
            folderId,
          }),
        },
        VaultDocumentSchema,
      );

      return doc;
    },
    // Invalidate the folder query (covers edge-case where the folder
    // didn't exist yet and was just auto-created server-side).
    onSuccess: () => qc.invalidateQueries({ queryKey: ATTACHMENTS_FOLDER_KEY }),
  });
}

// ─── Node document attach / detach ───────────────────────────────────────────

/**
 * Pure predicate: returns the polling interval (ms) while any document in the
 * list is still in a non-terminal state (`pending` or `processing`), or `false`
 * once all documents have reached a terminal state (`complete` or `failed`).
 *
 * Exported for direct unit testing — cleaner than driving fake timers through
 * renderHook.
 */
export function nodeDocumentsRefetchInterval(data: NodeDocumentView[] | undefined): number | false {
  if (!data || data.length === 0) return false;
  const hasInFlight = data.some(
    (doc) => doc.extractionStatus === "pending" || doc.extractionStatus === "processing",
  );
  return hasInFlight ? 2000 : false;
}

/** List all vault documents attached to a node. */
export function useNodeDocuments(nodeId: string) {
  return useQuery<NodeDocumentView[]>({
    queryKey: [...NODES_KEY, nodeId, "documents"],
    queryFn: () =>
      apiFetch<NodeDocumentView[]>(
        `/ideation-maps/nodes/${nodeId}/documents`,
        {},
        NodeDocumentViewSchema.array(),
      ),
    // Poll every 2 s while any doc is still extracting; stop once all terminal.
    refetchInterval: (query) => nodeDocumentsRefetchInterval(query.state.data),
  });
}

/**
 * Attach an existing vault document to a node.
 *
 * Node-agnostic: nodeId is supplied at mutate-time, not hook-time, so one hook
 * instance can serve BOTH the panel (Task 2.6) and canvas node-drop (Task 2.7).
 *
 * Call: `attach.mutateAsync({ nodeId, documentId })`
 * On success the per-node document list query for that nodeId is invalidated.
 */
export function useAttachNodeDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ nodeId, documentId }: { nodeId: string; documentId: string }) =>
      apiFetchUnchecked(`/ideation-maps/nodes/${nodeId}/documents`, {
        method: "POST",
        body: JSON.stringify({ documentId }),
      }),
    onSuccess: (_data, variables) =>
      qc.invalidateQueries({ queryKey: [...NODES_KEY, variables.nodeId, "documents"] }),
  });
}

/** Detach a vault document from a node (the mutate arg is the documentId). */
export function useDetachNodeDocument(nodeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) =>
      apiFetchUnchecked(`/ideation-maps/nodes/${nodeId}/documents/${documentId}`, {
        method: "DELETE",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...NODES_KEY, nodeId, "documents"] }),
  });
}

// ─── Clippings ───────────────────────────────────────────────────────────────

/**
 * Server-side SSRF-guarded URL fetch that extracts a page's og:description
 * snippet and title. Used by `ClippingsPanel` "From URL" mode — the browser
 * can't cross-origin-fetch pages directly, so this delegates to the same
 * `unfurlLink` guard used by the chat mention preview (ADR-0017).
 *
 * Returns `{ url, title, text }`. An empty `text` string means the server
 * couldn't extract anything useful; the UI surfaces this as a prompt to paste
 * the text manually instead.
 */
export function useFetchClipping() {
  return useMutation({
    mutationFn: (url: string) =>
      apiFetch<ClippingFetchResponse>(
        "/ideation-maps/clippings/fetch",
        { method: "POST", body: JSON.stringify({ url }) },
        ClippingFetchResponseSchema,
      ),
  });
}

// ─── Map hierarchy: theme proposal / decompose / flatten / reassign ──────────

export function useThemeProposal() {
  return useMutation({
    mutationFn: (mapId: string) =>
      apiFetch<ThemeProposal>(
        `/ideation-maps/maps/${mapId}/theme-proposal`,
        { method: "POST" },
        ThemeProposalSchema,
      ),
  });
}

export function useDecomposeMap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ mapId, proposal }: { mapId: string; proposal: ThemeProposal }) =>
      apiFetch<HubDecomposeResult>(
        `/ideation-maps/maps/${mapId}/decompose`,
        { method: "POST", body: JSON.stringify({ proposal }) },
        HubDecomposeResultSchema,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: IDEATION_MAPS_KEY }),
  });
}

export function useFlattenHub() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mapId: string) =>
      apiFetch<HubDecomposeResult>(
        `/ideation-maps/maps/${mapId}/flatten`,
        { method: "POST" },
        HubDecomposeResultSchema,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: IDEATION_MAPS_KEY }),
  });
}

export function useMoveNodeMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      nodeId,
      fromMapId,
      toMapId,
    }: {
      nodeId: string;
      fromMapId: string;
      toMapId: string;
    }) =>
      apiFetchUnchecked(`/ideation-maps/nodes/${nodeId}/membership`, {
        method: "PATCH",
        body: JSON.stringify({ fromMapId, toMapId }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: IDEATION_MAPS_KEY }),
  });
}

export function useSetNodeParent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      nodeId,
      parentNodeId,
      mapId,
    }: {
      nodeId: string;
      parentNodeId: string | null;
      mapId: string;
    }) =>
      apiFetch<IdeationMapNode>(
        `/ideation-maps/nodes/${nodeId}/parent`,
        { method: "PATCH", body: JSON.stringify({ mapId, parentNodeId }) },
        IdeationMapNodeSchema,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: IDEATION_MAPS_KEY }),
  });
}
